import _ from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useEffect, useState, useContext } from 'react';
import { Select } from '@bingads-webui-react/primitive';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withValidation, withError, withServerError } from '@bingads-webui-react/validation';
import { Input } from '@bingads-webui-campaign-react/fluent-with-validation';
import { defaultPhoneSchema, formatPhoneNumber } from '@bingads-webui-campaign-react/mca-core';

const getRegex = (format = '') => {
  const encode = (char) => {
    switch (char) {
      case '#':
        return '\\d';
      case '(':
      case ')':
        return `\\${char}`;
      default:
        return char;
    }
  };

  const str = ['^', ..._.map(format.split(''), encode), '$'].join('');
  const regex = new RegExp(str);

  return regex;
};

const repairInput = input => (
  (input || '').toString().replace(/\+?([^\n]*)/, '+$1')
);

const PhoneNumberPure = (props) => {
  const {
    id,
    className,
    schema,
    onChange,
    value: valueInProps,
    countryCode: propCountryCode,
    setCountryCode,
  } = props;

  const [innerSelectedCountryCode, innerSetSelectedCountryCode] = useState(undefined);
  const selectedCountryCode = propCountryCode || innerSelectedCountryCode;
  const setSelectedCountryCode = setCountryCode || innerSetSelectedCountryCode;
  const [onChangeMetric, setOnChangeMetric] = useState({});
  const i18n = useContext(I18nContext);

  const value = useMemo(() => repairInput(valueInProps), [valueInProps]);

  const enabledSchema = useMemo(() => (
    _.map(
      _.filter(schema || defaultPhoneSchema, s => s.disabled !== true),
      item => ({
        ...item,
        label: i18n.getString(item.label),
      })
    ).sort((item1, item2) => item1.label.localeCompare(item2.label))
  ), [i18n, schema]);

  const selectedSchema = useMemo(() => {
    if (!_.isEmpty(propCountryCode) && _.isFunction(setCountryCode)) {
      return _.find(
        enabledSchema,
        s => selectedCountryCode === s.key
      ) || defaultPhoneSchema[0];
    }
    return _.find(
      enabledSchema,
      s => value.indexOf(s.countryCode) === 0 || selectedCountryCode === s.key
    ) || defaultPhoneSchema[0];
  }, [enabledSchema, propCountryCode, selectedCountryCode, setCountryCode, value]);

  const countryCode = _.get(selectedSchema, 'countryCode');

  const selectedFormat = useMemo(() => {
    if (value.indexOf(countryCode) !== 0) {
      return selectedSchema.formats[0];
    }

    return _.find(
      selectedSchema.formats,
      f => getRegex(f).test(value.substr(countryCode.length).trim())
    ) || selectedSchema.formats[0];
  }, [countryCode, selectedSchema.formats, value]);

  const phoneNumber = useMemo(() => formatPhoneNumber(
    value.indexOf(countryCode) !== 0 ? value.replace(/\D/g, '') : value.substr(countryCode.length),
    selectedFormat
  ), [countryCode, selectedFormat, value]);

  useEffect(() => {
    if (onChangeMetric.cursor === 0 && onChangeMetric.beforeCursor === '') {
      onChangeMetric.event.target.setSelectionRange(0, 0);
    } else if (onChangeMetric.cursor && onChangeMetric.event && onChangeMetric.beforeCursor) {
      const diff = phoneNumber.indexOf(onChangeMetric.beforeCursor);
      if (diff !== -1) {
        onChangeMetric.event.target.setSelectionRange(
          diff + onChangeMetric.cursor,
          diff + onChangeMetric.cursor
        );
      }
      setOnChangeMetric({});
    }
  }, [phoneNumber, onChangeMetric]);

  const handleChange = useCallback((code, number) => {
    const newValue = _.isEmpty(number) ? '' : `${code} ${number}`;
    if (valueInProps !== newValue) {
      onChange({ target: { value: newValue } });
    }
  }, [valueInProps, onChange]);

  const handleCountryCodeChange = useCallback(({
    target: { value: newValue },
  }) => {
    setSelectedCountryCode(newValue);
    const prefix = _.get(_.find(enabledSchema, { key: newValue }), 'countryCode');
    handleChange(prefix, phoneNumber);
  }, [setSelectedCountryCode, enabledSchema, handleChange, phoneNumber]);

  const handlePhoneNumberChange = useCallback((event) => {
    event.persist();
    setOnChangeMetric({
      event,
      cursor: event.target.selectionStart,
      beforeCursor: event.target.value.substr(0, event.target.selectionStart),
    });
    handleChange(countryCode, formatPhoneNumber(event.target.value, selectedFormat));
  }, [countryCode, selectedFormat, handleChange]);

  useEffect(() => {
    if ((!_.isEmpty(phoneNumber) && value !== `${countryCode} ${formatPhoneNumber(phoneNumber)}`)
      || (_.isEmpty(phoneNumber) && value !== '')) {
      handleChange(countryCode, formatPhoneNumber(phoneNumber, selectedFormat));
    }
  }, [value, countryCode, phoneNumber, selectedFormat, handleChange]);

  return (
    <div style={{ display: 'flex' }} className="mca-phone-number">
      <Select
        id={id && `${id}-country-code`}
        className={classNames(className, 'select left')}
        value={selectedCountryCode || selectedSchema.key}
        onChange={handleCountryCodeChange}
        aria-label={i18n.getString(_TL_('Phone number country code'))}
      >
        {_.map(enabledSchema, item => (
          <option key={item.key} value={item.key}>
            {`${item.label} (${item.countryCode})`}
          </option>
        ))}
      </Select>
      <Input
        id={id && `${id}-phone-number`}
        className={classNames(className, 'text right')}
        value={phoneNumber}
        onChange={handlePhoneNumberChange}
        placeholder={selectedFormat}
        name={`${className}-PhoneNumber`}
        serverErrorProps={{ fieldSelector: 'PhoneNumber' }}
      />
    </div >
  );
};

PhoneNumberPure.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  schema: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    countryCode: PropTypes.string.isRequired,
    formats: PropTypes.arrayOf(PropTypes.string).isRequired,
  })),
  countryCode: PropTypes.string,
  setCountryCode: PropTypes.func,
};

PhoneNumberPure.defaultProps = {
  id: null,
  className: null,
  value: null,
  onChange: _.noop,
  schema: null,
  countryCode: null,
  setCountryCode: null,
};

export const PhoneNumber = withError(withValidation(PhoneNumberPure));
export const PhoneNumberWithServerError = withServerError(PhoneNumber);
